import React, {
  useContext,
  useCallback,
  useEffect,
  useState,
  useRef
} from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { LayoutContext } from "App/common/hoc/Layout";
import { SPopupContext } from "App/common/hoc/SlidingPopup";
import { decodeHandler, deepCompare, forbidExtraProps } from "utils";
import { CONTENT, NOTIFICATIONS, HTTP_EXCEPTION_MESSAGES } from "consts";

import {
  saveBackupSettingsStart,
  initiateBackup,
  initiateFullBackup,
  removeMachineStart,
  addClientsToPlanStart
} from "store/actions";

function Footer(props) {
  const { lState, lMethods } = useContext(LayoutContext);
  const { sPopupState, sPopupMethods } = useContext(SPopupContext);
  const lStateRef = useRef();
  const sPopupRef = useRef();

  const [enableDisabled, setEnableDisabled] = useState(false);

  const isOffline = props.client.machineStatus === "offline";
  const isInitProgress = props.client.init.status;
  const inProgress =
    props.client.status.value === props.enums.BackupScheduleStatus.IN_PROGRESS;
  const restoreInProgress = props.client.restoreStatus;
  // no full image backup if image/file restore is in progress
  const fileRestoreInProgress =
    props.client?.restoreStatus &&
    !("totalFiles" in props.client.restoreStatus);
  const crossRestoreInProgress =
    lState.slidingPopup.params.crossClientRestoreProgress;

  /* closing sliding popup */
  if (
    props.client.init.status &&
    lState.slidingPopup.show &&
    !props.client.init.holdSliderOpen
  ) {
    lMethods.closeSlidingPopup();
  }

  /*Calculate computer limit and notify the same*/
  useEffect(() => {
    const clientListLength = Object.keys(props.clientList).filter(
      val => props.clientList[val].isConfigured
    ).length;

    if (
      ((props.computerLimit <= clientListLength &&
        props.planType !== "Paid" &&
        props.ecommerceFlag !== "S") ||
        (sPopupState.notify.params &&
          sPopupState.notify.params.msg ===
            HTTP_EXCEPTION_MESSAGES.CLIENT_QUOTA_EXCEEDED)) &&
      lState.slidingPopup.alertName === "enablePC"
    ) {
      setEnableDisabled(true);
      sPopupMethods.showNotify({
        msg: NOTIFICATIONS.enableMachine.computerLimit,
        status: "warning",
        upgrade: true,
        customClass: "max-computers-note"
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.notification]);

  /*****************************Handling popup using enter key ***************************/

  useEffect(() => {
    lStateRef.current = lState;
    sPopupRef.current = sPopupState;
    const handleKeyDown = e => {
      if (
        e.key === "Enter" &&
        lStateRef.current.slidingPopup.show &&
        sPopupRef.current.popup.show
      ) {
        if (sPopupRef.current.popup.alertName === "removeMachine") {
          sPopupRef.current.popup.params.execute();
        }

        if (sPopupRef.current.popup.alertName === "saveOrDiscardChanges") {
          sPopupRef.current.popup.params.execute();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    // Cleanup: Remove the event listener when the component unmounts
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [lState, sPopupState]);

  const saveSettings = useCallback(() => {
    let addDefaultPlan = true;
    const backupSettings = JSON.parse(JSON.stringify(props.backupSettings));

    //cached settings check for default plan true
    const cachedSettings = JSON.parse(JSON.stringify(props.cachedSettings));
    if (cachedSettings !== null && !deepCompare(backupSettings, cachedSettings))
      addDefaultPlan = false;

    delete backupSettings.loading;
    delete backupSettings.notification;

    if (backupSettings.encryption)
      backupSettings.encryption.key = backupSettings.encryption.key.value;
    else backupSettings.encryption.key = "";

    backupSettings.scheduleInfo.startTime =
      backupSettings.scheduleInfo.startTime.value;
    backupSettings.scheduleInfo.type = backupSettings.scheduleInfo.type.current;

    if (backupSettings.emailNotification.emails.list.length > 0) {
      backupSettings.emailNotification.enable = true;
    } else {
      backupSettings.emailNotification.enable = false;
    }

    delete backupSettings.emailNotification.enabled;
    backupSettings.emailNotification.emails =
      backupSettings.emailNotification.emails.list;
    if (
      backupSettings.emailNotification.operationStatus.onSuccess &&
      backupSettings.emailNotification.operationStatus.onFailure
    ) {
      backupSettings.emailNotification.operationStatus = 0;
    } else {
      if (backupSettings.emailNotification.operationStatus.onSuccess)
        backupSettings.emailNotification.operationStatus = 1;
      else if (
        !backupSettings.emailNotification.operationStatus.onSucces &&
        backupSettings.emailNotification.operationStatus.onFailure
      )
        backupSettings.emailNotification.operationStatus = 2;
      else backupSettings.emailNotification.operationStatus = 3;
    }

    const volumes = [];
    if (Object.keys(backupSettings.volumes.list).length > 0) {
      for (let key in backupSettings.volumes.list) {
        const temp = {
          deviceId: backupSettings.volumes.list[key].deviceId,
          volume: []
        };
        for (let volKey in backupSettings.volumes.list[key].volume) {
          temp.volume.push(backupSettings.volumes.list[key].volume[volKey]);
        }

        volumes.push(temp);
      }
    }

    backupSettings.volumes = volumes;
    backupSettings.log = JSON.stringify({
      mode: "new",
      params: CONTENT.appLogs.backupSettings.newConfigured
    });

    if (addDefaultPlan) {
      props.addClientsToPlanStart(
        {
          devices: [
            {
              clientName: backupSettings.clientDetails.name,
              clientUUID: backupSettings.clientDetails.clientUUID
            }
          ],
          planId: 1,
          log: JSON.stringify({
            mode: "new",
            params: "New Computer Configured"
          })
        },
        true,
        backupSettings.clientDetails.serverIPAddress
      );
    } else {
      props.saveBackupSettingsStart(backupSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteMachine = () => {
    sPopupMethods.openPopup("removeMachine", {
      body: {
        CLIENT_NAME: decodeHandler(props.backupSettings.clientDetails.name)
      },
      execute: () => {
        props.removeMachineStart(props.backupSettings.clientDetails.clientUUID);
        sPopupMethods.closePopup();
      }
    });
  };

  return (
    <div className="slider_content_footer">
      <div className="slider_summary_btns">
        <div className="slider_summary_btns__inner">
          {lState.slidingPopup.alertName === "settings" ? (
            <React.Fragment>
              <div className="backup_image_btns just_flex">
                <div
                  className={[
                    "slider_btn full_img_backup_btn",
                    isInitProgress ||
                    isOffline ||
                    inProgress ||
                    fileRestoreInProgress ||
                    (props.expiryDays < 1 && props.planType === "Trial")
                      ? "btn_disable"
                      : "btn_primary"
                  ].join(" ")}
                  onClick={() => {
                    if (
                      !(
                        isInitProgress ||
                        isOffline ||
                        inProgress ||
                        fileRestoreInProgress ||
                        (props.expiryDays < 1 && props.planType === "Trial")
                      )
                    ) {
                      if (props.client.lastBackupTime === 0) {
                        props.initiateFullBackup(
                          props.backupSettings.clientDetails.clientUUID
                        );
                        sPopupMethods.closePopup();
                      } else {
                        sPopupMethods.openPopup("startFullBackup", {
                          execute: () => {
                            props.initiateFullBackup(
                              props.backupSettings.clientDetails.clientUUID
                            );
                            sPopupMethods.closePopup();
                          }
                        });
                      }
                    }
                  }}
                >
                  Start Full Image Backup
                </div>
                {/* <div
                  className={[
                    "slider_btn full_img_backup_btn",
                    isInitProgress ||
                    isOffline ||
                    inProgress || restoreInProgress ||
                    props.expiryDays < 1
                      ? "btn_disable"
                      : "btn_primary"
                  ].join(" ")}
                  onClick={() =>
                    isInitProgress ||
                    isOffline ||
                    inProgress || restoreInProgress ||
                    props.expiryDays < 1
                      ? {}
                      : props.initiateBackup(
                          props.backupSettings.clientDetails.clientUUID
                        )
                  }
                >
                  Start Incremental backup
                </div> */}
              </div>
              <div
                className={[
                  "slider_btn",
                  isInitProgress
                    ? "btn_disable"
                    : "slider_btn_delete remove_machine_btn"
                ].join(" ")}
                onClick={() =>
                  isInitProgress
                    ? {}
                    : props.virtAPIDown.show
                    ? sPopupMethods.openPopup("removeMachineVirtAPIError", {
                        execute: () => {
                          sPopupMethods.closePopup();
                        }
                      })
                    : props.vmsList.filter(
                        vm =>
                          vm.client_uuid ===
                          props.backupSettings.clientDetails.clientUUID
                      ).length > 0
                    ? sPopupMethods.openPopup("removeMachineWarningForVM", {
                        execute: () => {
                          sPopupMethods.closePopup();
                        }
                      })
                    : deleteMachine()
                }
              >
                Delete
              </div>
            </React.Fragment>
          ) : (
            lState.slidingPopup.alertName === "enablePC" && (
              <React.Fragment>
                <div className="backup_image_btns just_flex">
                  <div
                    className={[
                      "slider_btn enable_btn",
                      enableDisabled ? "btn_disable" : "btn_primary"
                    ].join(" ")}
                    // onClick={saveSettings}
                    onClick={() => {
                      if (!enableDisabled) {
                        setEnableDisabled(true);
                        saveSettings();
                      }
                    }}
                  >
                    Enable now
                  </div>
                </div>
                <div
                  className={[
                    "slider_btn",
                    isInitProgress || inProgress || restoreInProgress
                      ? "btn_disable"
                      : "slider_btn_delete remove_machine_btn"
                  ].join(" ")}
                  onClick={() =>
                    isInitProgress || inProgress || restoreInProgress
                      ? {}
                      : sPopupMethods.openPopup("removeMachine", {
                          execute: () => {
                            props.removeMachineStart(
                              props.backupSettings.clientDetails.clientUUID
                            );
                            sPopupMethods.closePopup();
                          },
                          body: {
                            CLIENT_NAME: decodeHandler(
                              props.backupSettings.clientDetails.name
                            )
                          }
                        })
                  }
                >
                  Delete
                </div>
              </React.Fragment>
            )
          )}
        </div>
      </div>
    </div>
  );
}

Footer.propTypes = forbidExtraProps(
  {
    backupSettings: PropTypes.object.isRequired,
    expiryDays: PropTypes.number,
    planType: PropTypes.string,
    clientList: PropTypes.object.isRequired,
    computerLimit: PropTypes.number.isRequired,
    notification: PropTypes.object.isRequired,
    cachedSettings: PropTypes.object,
    vmsList: PropTypes.arrayOf(PropTypes.object)
  },
  [
    "saveBackupSettingsStart",
    "initiateBackup",
    "initiateFullBackup",
    "removeMachineStart",
    "enums",
    "statusKey",
    "client"
  ]
);

function mapStateToProps(state) {
  return {
    backupSettings: state.backupSettings,
    enums: state.common.enums,
    expiryDays: state.account.expiryDays,
    planType: state.account.plan && state.account.plan.planType,
    clientList: state.dashboard.clients.list,
    computerLimit: state.account.plan && state.account.plan.computerLimit,
    notification: state.common.notification,
    ecommerceFlag: state.account.ecommerceFlag,
    cachedSettings: state.common.backupSettings,
    vmsList: state.virtualization.vmsList,
    virtAPIDown: state.virtualization.serverDownPage
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      saveBackupSettingsStart,
      initiateBackup,
      initiateFullBackup,
      removeMachineStart,
      addClientsToPlanStart
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
